import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ShopBottomNav from '../../components/ShopBottomNav';
import { getProfile, saveProfile } from '../../services/shopState';
import { geocodeOne } from '../../services/locationService';
import {
  updateMyProfile,
  getMyProfile,
  getAreaLookup,
  findCoveringArea,
  requestAreaCoverage,
} from '../../services/apiService';
import { DEAL_CATEGORIES } from '../../constants/categories';
import './ShopProfilePage.css';

// Wait this long after the last keystroke before looking the Winkeladres up.
// Nominatim allows ~1 request/second and no per-keystroke autocomplete, so the
// lookup only fires once the retailer pauses on a complete-looking address.
const GEOCODE_DEBOUNCE_MS = 1000;

// "Complete enough to look up": a street with a house number plus a place or
// postcode. Stops half-typed input from being geocoded to the wrong street.
function looksComplete(address) {
  const a = (address || '').trim();
  if (a.length < 8 || !/\d/.test(a)) return false;
  const hasPostcode = /\b\d{4}\s?[a-z]{2}\b/i.test(a);
  const words = a.replace(/[\d,]/g, ' ').split(/\s+/).filter((w) => w.length > 1);
  return hasPostcode || words.length >= 2;
}

// Coverage requests already sent from this browser, so re-saving the same
// address does not mail the admins again.
const COVERAGE_SENT_KEY = 'walkin_coverage_requests_sent';
function coverageAlreadySent(address) {
  try {
    const list = JSON.parse(localStorage.getItem(COVERAGE_SENT_KEY) || '[]');
    return list.includes(address.trim().toLowerCase());
  } catch { return false; }
}
function rememberCoverageSent(address) {
  try {
    const list = JSON.parse(localStorage.getItem(COVERAGE_SENT_KEY) || '[]');
    list.push(address.trim().toLowerCase());
    localStorage.setItem(COVERAGE_SENT_KEY, JSON.stringify(list.slice(-50)));
  } catch { /* ignore */ }
}

// The session profile is overwritten with just name/area/email on every login
// (ShopLoginPage), while the shop record in localStorage survives. Anything the
// retailer saved earlier — their exact location above all — is read back from
// there so the form does not look empty after signing in again.
function readShopRecord() {
  const email = (sessionStorage.getItem('walkin_shop_email') || '').toLowerCase();
  if (!email) return null;
  for (const key of ['walkin_shops', 'walkin_admin_shops']) {
    try {
      const list = JSON.parse(localStorage.getItem(key) || '[]');
      const found = list.find((s) => (s.email || '').toLowerCase() === email);
      if (found) return found;
    } catch { /* ignore */ }
  }
  return null;
}

// Same list the deal pages and the walker feed use (src/constants/categories).
const CATEGORIES = DEAL_CATEGORIES;

const DAYS = [
  ['mon', 'Maandag'  ],
  ['tue', 'Dinsdag'  ],
  ['wed', 'Woensdag' ],
  ['thu', 'Donderdag'],
  ['fri', 'Vrijdag'  ],
  ['sat', 'Zaterdag' ],
  ['sun', 'Zondag'   ],
];

const defaults = () => {
  const areaName = sessionStorage.getItem('walkin_shop_area') || '';
  return {
    shopName: sessionStorage.getItem('walkin_shop_name') || 'Mijn winkel',
    email: sessionStorage.getItem('walkin_shop_email') || '',
    category: '',
    areaName,
    address: '',
    postcode: '',
    city: '',
    description: '',
    instructions: '',
    lat: null,
    lng: null,
    locationVerified: false,
    locationAddress: '',
    hours: {
      mon: { open: '09:00', close: '18:00', closed: false },
      tue: { open: '09:00', close: '18:00', closed: false },
      wed: { open: '09:00', close: '18:00', closed: false },
      thu: { open: '09:00', close: '18:00', closed: false },
      fri: { open: '09:00', close: '18:00', closed: false },
      sat: { open: '10:00', close: '17:00', closed: false },
      sun: { open: '',      close: '',      closed: true  },
    },
    closedDays: [],
  };
};

// The one address string the backend keeps — postcode and city have no column
// of their own, so they are folded in, the same way the signup forms do it.
// Also what gets geocoded, so the coordinates always describe the address the
// retailer actually saved.
function addressLine(p) {
  return [p.address, [p.postcode, p.city].filter(Boolean).join(' ')]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(', ');
}

export default function ShopProfilePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [newDay, setNewDay] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Winkeladres lookup: idle | searching | found | notfound | error
  const [geoState, setGeoState] = useState('idle');
  // The gebied the account belongs to (set by an admin) and the gebied the
  // Winkeladres actually falls in. `areasLoaded` guards the "no gebied"
  // warning: with no area list there is nothing to compare against.
  const [myArea, setMyArea] = useState(null);
  const [areas, setAreas] = useState([]);
  const [areasLoaded, setAreasLoaded] = useState(false);
  const [coverageNotice, setCoverageNotice] = useState('');
  const geoSeq = useRef(0);

  useEffect(() => {
    const p = { ...defaults(), ...(getProfile() || {}) };
    const record = readShopRecord();
    if (record) {
      // Only fill gaps — a value already in the session profile is the one the
      // retailer is currently editing.
      for (const field of [
        'address', 'postcode', 'city', 'description', 'instructions',
        'locationAddress', 'category',
      ]) {
        if (!p[field] && record[field]) p[field] = record[field];
      }
      if (p.lat == null || p.lng == null) {
        if (record.lat != null && record.lng != null) {
          p.lat = record.lat;
          p.lng = record.lng;
          p.locationVerified = !!record.locationVerified;
        }
      }
    }
    if (!p.closedDays) p.closedDays = [];
    // Every day has to be present with all three fields: the form renders all
    // seven and reads hours[key].closed directly, so a profile carrying only
    // some of them — now possible, since the backend stores openingHours and
    // can hand back whatever was written — took the whole page down.
    p.hours = Object.fromEntries(
      Object.entries(defaults().hours).map(
        ([key, day]) => [key, { ...day, ...(p.hours?.[key] || {}) }]
      )
    );
    if (!p.areaName) p.areaName = defaults().areaName;
    // The Winkeladres is ONE line (the backend keeps one address string).
    // Older profiles kept postcode/city apart; fold them in once.
    const line = addressLine(p);
    if (line !== p.address) {
      p.address = line;
      p.postcode = '';
      p.city = '';
    }
    if (p.lat != null && p.lng != null) setGeoState('found');
    setProfile(p);
  }, []);

  // The account's gebied (and its gebiedsadres/-code) come from the backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const lookup = await getAreaLookup();
      let me = null;
      try { me = await getMyProfile(); } catch { /* offline — keep session values */ }
      if (cancelled) return;
      const list = [...lookup.values()];
      setAreas(list);
      setAreasLoaded(list.length > 0);
      const area = (me?.areaId && lookup.get(me.areaId))
        || list.find((a) => a.name === sessionStorage.getItem('walkin_shop_area'))
        || null;
      setMyArea(area);
    })();
    return () => { cancelled = true; };
  }, []);

  // Winkeladres → GPS, automatically once the address looks complete.
  useEffect(() => {
    if (!profile || !profile._addressDirty) return undefined;
    const address = (profile.address || '').trim();
    if (!looksComplete(address)) {
      setGeoState('idle');
      return undefined;
    }
    const seq = ++geoSeq.current;
    setGeoState('searching');
    const t = setTimeout(async () => {
      try {
        const hit = await geocodeOne(address);
        if (seq !== geoSeq.current) return; // a newer keystroke won
        if (!hit) { setGeoState('notfound'); return; }
        setProfile((prev) => ({
          ...prev,
          lat: hit.lat,
          lng: hit.lng,
          locationVerified: true,
          locationAddress: hit.displayName,
          _addressDirty: false,
        }));
        setGeoState('found');
      } catch {
        if (seq === geoSeq.current) setGeoState('error');
      }
    }, GEOCODE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [profile?.address, profile?._addressDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Which gebied (if any) covers the shop's coordinates.
  const covering = profile && areasLoaded && profile.lat != null && profile.lng != null
    ? findCoveringArea(profile.lat, profile.lng, areas)
    : null;
  const outsideEveryArea = !!profile && areasLoaded && profile.lat != null
    && profile.lng != null && !covering;

  if (!profile) return null;

  const handleField = (key, value) => {
    setProfile((prev) => {
      const next = { ...prev, [key]: value };
      // A new Winkeladres invalidates the old coordinates straight away; the
      // debounced lookup above fills in the new ones.
      if (key === 'address') {
        next.postcode = '';
        next.city = '';
        next.lat = null;
        next.lng = null;
        next.locationVerified = false;
        next.locationAddress = '';
        next._addressDirty = true;
      }
      return next;
    });
    setSaved(false);
    setCoverageNotice('');
  };

  const setHour = (day, key, value) => {
    setProfile({
      ...profile,
      hours: { ...profile.hours, [day]: { ...profile.hours[day], [key]: value } },
    });
    setSaved(false);
  };

  const addClosedDay = () => {
    if (!newDay.trim()) return;
    setProfile({ ...profile, closedDays: [...profile.closedDays, newDay.trim()] });
    setNewDay('');
    setSaved(false);
  };

  const removeClosedDay = (i) => {
    setProfile({
      ...profile,
      closedDays: profile.closedDays.filter((_, idx) => idx !== i),
    });
    setSaved(false);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!profile.category) {
      setSaveError('Kies een categorie voordat je opslaat.');
      return;
    }
    setSaving(true);
    setSaveError('');

    // A saved address with no coordinates is the case that left every shop in
    // a winkelcentrum sitting on the gebied's centre — same point on the map,
    // same distance for every walker. So resolve them here rather than
    // waiting for the retailer to run the address search below by hand.
    let next = profile;
    if ((next.lat == null || next.lng == null) && addressLine(next)) {
      try {
        const hit = await geocodeOne(addressLine(next));
        if (hit) {
          next = {
            ...next,
            lat: hit.lat,
            lng: hit.lng,
            locationVerified: true,
            locationAddress: hit.displayName,
          };
          setProfile(next);
        }
      } catch {
        // Nominatim is a third party; saving the rest of the profile matters
        // more than the coordinates.
      }
    }
    const { _addressDirty, ...toStore } = next;
    next = toStore;
    saveProfile(next);
    sessionStorage.setItem('walkin_shop_name', next.shopName);
    if (next.email) sessionStorage.setItem('walkin_shop_email', next.email);
    if (next.areaName) sessionStorage.setItem('walkin_shop_area', next.areaName);

    // Mirror profile-editable fields onto the canonical shop record so
    // customers (DealPage) see the latest instructions / address.
    const email = (next.email || sessionStorage.getItem('walkin_shop_email') || '').toLowerCase();
    if (email) {
      const patch = {
        shopName: next.shopName,
        category: next.category || '',
        address: next.address,
        postcode: next.postcode,
        city: next.city,
        description: next.description,
        instructions: next.instructions || '',
        // Deal creation reads the coordinates from here for accounts the
        // backend has none for.
        lat: next.lat ?? null,
        lng: next.lng ?? null,
        locationVerified: !!next.locationVerified,
        locationAddress: next.locationAddress || '',
      };
      for (const key of ['walkin_shops', 'walkin_admin_shops']) {
        let list = [];
        try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch { list = []; }
        const updated = list.map((s) =>
          (s.email || '').toLowerCase() === email ? { ...s, ...patch } : s
        );
        localStorage.setItem(key, JSON.stringify(updated));
      }
      window.dispatchEvent(new Event('walkin-admin-shops-change'));
    }

    // The backend keeps the address every walker sees and, since 2026-09-06,
    // the shop's coordinates — so the fields the retailer typed here have to
    // reach it. Name and email are deliberately left out: they default to
    // placeholders in this form and would overwrite the real account values.
    const apiPatch = { lat: next.lat ?? null, lng: next.lng ?? null };
    if (next.description !== undefined) apiPatch.description = next.description || '';
    if (next.instructions !== undefined) apiPatch.instructions = next.instructions || '';
    if (next.category) apiPatch.category = next.category;
    // Sent under the day keys the signup form already uses; shopState reads
    // either those or the Dutch ones. The walker's deal page gets these from
    // r.instructions / r.opening_hours on the deal row.
    if (next.hours) apiPatch.openingHours = next.hours;
    const line = addressLine(next);
    if (line) apiPatch.address = line;

    // Outside every gebied → let the WalkIn admins know (e-mail, sent by the
    // backend), once per address.
    if (areasLoaded && next.lat != null && next.lng != null && line
        && !findCoveringArea(next.lat, next.lng, areas) && !coverageAlreadySent(line)) {
      requestAreaCoverage({
        address: line,
        lat: next.lat,
        lng: next.lng,
        shopName: next.shopName,
        email: next.email,
      }).then(({ sent }) => {
        if (sent) {
          rememberCoverageSent(line);
          setCoverageNotice('We hebben het Walkin Deals-team op de hoogte gebracht.');
        }
      });
    }

    try {
      await updateMyProfile(apiPatch);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (err) {
      // Everything above is already stored locally, so the retailer does not
      // lose their edits — but the walker side will not see them yet.
      setSaveError(
        err?.message
          ? `Opslaan bij WalkIn mislukt: ${err.message}`
          : 'Opslaan bij WalkIn mislukt. Probeer het later opnieuw.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="shop-profile">
      <header className="shop-profile__header">
        <button
          className="shop-profile__back"
          onClick={() => navigate(-1)}
          aria-label="Terug"
        >
          ←
        </button>
        <h1 className="shop-profile__title">Profiel</h1>
        <span className="shop-profile__spacer" />
      </header>

      <main className="shop-profile__main">
        {/* WINKEL INFO */}
        <section className="shop-profile__card">
          <div className="shop-profile__card-label">Winkel info</div>
          <label className="shop-profile__field">
            <span>Naam</span>
            <input
              className="shop-profile__input"
              value={profile.shopName}
              onChange={(e) => handleField('shopName', e.target.value)}
            />
          </label>
          <label className="shop-profile__field">
            <span>Categorie *</span>
            <select
              className="shop-profile__input"
              value={profile.category || ''}
              onChange={(e) => handleField('category', e.target.value)}
            >
              <option value="">Kies een categorie…</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <small className="shop-profile__hint">
              Verplicht. Je deals nemen deze categorie automatisch over.
            </small>
          </label>
          {/* GEBIEDSADRES — read-only, set by the admin with the gebied */}
          <div className="shop-profile__field">
            <span>Gebiedsadres</span>
            <div className="shop-profile__readonly">
              {myArea ? (
                <>
                  <strong>{myArea.name}</strong>
                  {myArea.address && <span>{myArea.address}</span>}
                  <span className="shop-profile__readonly-meta">
                    Gebiedscode {myArea.code}
                  </span>
                </>
              ) : (
                <span>{profile.areaName || 'Nog niet aan een gebied gekoppeld'}</span>
              )}
            </div>
            <small className="shop-profile__hint">
              Het gebied (bijv. een winkelcentrum) waar je winkel onder valt.
              Wordt door Walkin Deals ingesteld.
            </small>
          </div>

          {/* WINKELADRES — editable; GPS is resolved automatically */}
          <label className="shop-profile__field">
            <span>Winkeladres</span>
            <input
              className="shop-profile__input"
              value={profile.address}
              placeholder="Straat + huisnummer, postcode en plaats"
              autoComplete="street-address"
              onChange={(e) => handleField('address', e.target.value)}
            />
            {geoState === 'searching' && (
              <small className="shop-profile__geo shop-profile__geo--busy">Locatie bepalen…</small>
            )}
            {geoState === 'notfound' && (
              <small className="shop-profile__geo shop-profile__geo--error">
                Adres niet gevonden. Controleer straat, huisnummer en plaats.
              </small>
            )}
            {geoState === 'error' && (
              <small className="shop-profile__geo shop-profile__geo--error">
                Locatie kon niet worden bepaald (geen verbinding). Bij opslaan proberen we het opnieuw.
              </small>
            )}
            {geoState === 'idle' && profile.lat == null && (
              <small className="shop-profile__hint">
                Vul het volledige adres in; de GPS-locatie wordt automatisch bepaald.
              </small>
            )}
            {profile.lat != null && profile.lng != null && (
              <div className="shop-profile__loc-found">
                <div className="shop-profile__loc-found-head">✅ Locatie ingesteld</div>
                {profile.locationAddress && (
                  <div className="shop-profile__loc-address">{profile.locationAddress}</div>
                )}
                <div className="shop-profile__loc-coords">
                  {Number(profile.lat).toFixed(5)}, {Number(profile.lng).toFixed(5)}
                </div>
                {covering && (
                  <div className="shop-profile__loc-area">
                    Valt binnen gebied <strong>{covering.area.name}</strong>
                    {myArea && myArea.id !== covering.area.id && (
                      <> — je account is gekoppeld aan {myArea.name}. Neem contact op met Walkin Deals als dit niet klopt.</>
                    )}
                  </div>
                )}
              </div>
            )}
            {outsideEveryArea && (
              <div className="shop-profile__no-area" role="alert">
                <strong>Nog geen gebied voor dit adres</strong>
                <span>
                  Voor dit winkeladres is nog geen gebied bij Walkin Deals gedefinieerd.
                  Je deals zijn daardoor nog niet zichtbaar voor bezoekers in de buurt.
                  Zodra je opslaat, krijgt het Walkin Deals-team automatisch een melding
                  om een gebied aan te maken.
                </span>
                {coverageNotice && <em>{coverageNotice}</em>}
              </div>
            )}
          </label>

          <label className="shop-profile__field">
            <span>Beschrijving</span>
            <textarea
              className="shop-profile__textarea"
              value={profile.description}
              onChange={(e) => handleField('description', e.target.value.slice(0, 200))}
              maxLength={200}
              rows={3}
            />
          </label>
          <label className="shop-profile__field">
            <span>Looproute instructies (optioneel)</span>
            <textarea
              className="shop__instructions-input"
              placeholder="bijv: Ingang Noord · Verdieping 1 · Naast de H&M · Volg de borden naar 'Mode'"
              value={profile.instructions || ''}
              onChange={(e) => handleField('instructions', e.target.value.slice(0, 300))}
              maxLength={300}
              rows={3}
            />
            <div className="shop__char-count">
              {(profile.instructions || '').length}/300
            </div>
            <div className="shop__instructions-examples">
              <p>Handig voor winkels in een gebouw of winkelcentrum. Voorbeelden:</p>
              <span onClick={() => handleField('instructions', 'Verdieping 1 · Ingang Noord · Naast de H&M')}>
                🏬 Winkelcentrum
              </span>
              <span onClick={() => handleField('instructions', 'Begane grond · Naast de lift · Volg borden Foodcourt')}>
                🍽️ Foodcourt
              </span>
              <span onClick={() => handleField('instructions', 'Parkeergarage ingang B · Lift naar BG')}>
                🚗 Parkeergarage
              </span>
            </div>
          </label>
        </section>

        {/* OPENINGSTIJDEN */}
        <section className="shop-profile__card">
          <div className="shop-profile__card-label">Openingstijden</div>
          {DAYS.map(([key, label]) => (
            <div key={key} className="shop-profile__hour">
              <span className="shop-profile__day">{label}</span>
              <label className="shop-profile__closed">
                <input
                  type="checkbox"
                  checked={profile.hours[key].closed}
                  onChange={(e) => setHour(key, 'closed', e.target.checked)}
                />
                Gesloten
              </label>
              <input
                type="time"
                className="shop-profile__time"
                value={profile.hours[key].open}
                disabled={profile.hours[key].closed}
                onChange={(e) => setHour(key, 'open', e.target.value)}
              />
              <span>–</span>
              <input
                type="time"
                className="shop-profile__time"
                value={profile.hours[key].close}
                disabled={profile.hours[key].closed}
                onChange={(e) => setHour(key, 'close', e.target.value)}
              />
            </div>
          ))}
        </section>

        {/* SLUITINGSDAGEN */}
        <section className="shop-profile__card">
          <div className="shop-profile__card-label">Sluitingsdagen</div>
          <ul className="shop-profile__closed-list">
            {profile.closedDays.length === 0 && (
              <li className="shop-profile__closed-empty">Geen sluitingsdagen.</li>
            )}
            {profile.closedDays.map((day, i) => (
              <li key={i} className="shop-profile__closed-item">
                <span>{day}</span>
                <button onClick={() => removeClosedDay(i)} aria-label="Verwijder">✕</button>
              </li>
            ))}
          </ul>
          <div className="shop-profile__closed-add">
            <input
              className="shop-profile__input"
              placeholder="bijv. 25-12-2026 Eerste Kerstdag"
              value={newDay}
              onChange={(e) => setNewDay(e.target.value)}
            />
            <button className="shop-profile__add-btn" onClick={addClosedDay}>
              Toevoegen
            </button>
          </div>
        </section>

        <button
          className="shop-profile__save"
          onClick={handleSave}
          disabled={saving || !profile.category}
        >
          {saving ? 'Opslaan…' : 'Wijzigingen opslaan'}
        </button>
        {saved && <div className="shop-profile__saved">✅ Opgeslagen</div>}
        {saveError && <div className="shop-profile__save-error">{saveError}</div>}
      </main>

      <ShopBottomNav />
    </div>
  );
}
